import {
  ACCESSORIES,
  type Avatar,
  BACKGROUND_COLORS,
  DEFAULT_AVATAR,
  EARS,
  EYE_COLORS,
  EYES,
  FACE_SHAPES,
  FUR_COLORS,
  MOUTHS,
  PATTERNS,
  randomAvatar,
  WHISKERS,
} from "@talkers/shared";
import { For, type JSX } from "solid-js";
import { CatAvatar } from "@/components/CatAvatar";
import { Button, HStack, Stack } from "@/components/ui";

/**
 * 猫アバターのビルダー。
 *
 * 形のパーツは「今のアバターにそのパーツだけ差し替えたプレビュー」を並べる。
 * 選択肢を名前だけで選ばせると結果が想像できないため、見たまま選べるようにする。
 * 色は同じ方式だと差が分かりにくいので色見本にする。
 */
type ColorDef = { readonly label: string; readonly base: string; readonly shade: string };

/**
 * プレビューの基準になる猫。
 *
 * 「今のアバター」を基準にすると、他の欄で選んだ柄やアクセサリが全部の選択肢に写り込み、
 * そのパーツだけの違いが読み取れない。基準を固定して差分だけを見せる。
 * 実際の組み合わせは左の大きいプレビューで確認する。
 */
const PREVIEW_BASE: Avatar = {
  ...DEFAULT_AVATAR,
  pattern: "solid",
  accessory: "none",
  background: "none",
};

function colorOptions<T extends string>(record: Record<T, ColorDef>) {
  return (Object.entries(record) as [T, ColorDef][]).map(([id, def]) => ({
    id,
    label: def.label,
    base: def.base,
    shade: def.shade,
  }));
}

function ShapeRow<T extends string>(props: {
  label: string;
  options: readonly { readonly id: T; readonly label: string }[];
  selected: T;
  preview: (id: T) => Avatar;
  onSelect: (id: T) => void;
}): JSX.Element {
  return (
    <fieldset class="part-group">
      <legend class="part-legend">{props.label}</legend>
      <div class="part-options">
        <For each={props.options}>
          {(option) => (
            <button
              type="button"
              class="part-option"
              classList={{ "part-option--selected": props.selected === option.id }}
              aria-pressed={props.selected === option.id}
              onClick={() => props.onSelect(option.id)}
            >
              <CatAvatar avatar={props.preview(option.id)} size={44} label={option.label} />
              <span class="part-option-label">{option.label}</span>
            </button>
          )}
        </For>
      </div>
    </fieldset>
  );
}

function ColorRow<T extends string>(props: {
  label: string;
  options: readonly {
    readonly id: T;
    readonly label: string;
    readonly base: string;
    readonly shade: string;
  }[];
  selected: T;
  onSelect: (id: T) => void;
}): JSX.Element {
  return (
    <fieldset class="part-group">
      <legend class="part-legend">{props.label}</legend>
      <div class="part-options">
        <For each={props.options}>
          {(option) => (
            <button
              type="button"
              class="color-option"
              classList={{
                "color-option--selected": props.selected === option.id,
                "color-option--empty": option.base === "transparent",
              }}
              aria-pressed={props.selected === option.id}
              aria-label={option.label}
              title={option.label}
              onClick={() => props.onSelect(option.id)}
              style={
                option.base === "transparent"
                  ? undefined
                  : {
                      background: `linear-gradient(135deg, ${option.base} 0 55%, ${option.shade} 55% 100%)`,
                    }
              }
            />
          )}
        </For>
      </div>
    </fieldset>
  );
}

export function AvatarBuilder(props: {
  value: Avatar;
  onChange: (avatar: Avatar) => void;
}): JSX.Element {
  const patch = <K extends keyof Avatar>(key: K, value: Avatar[K]) =>
    props.onChange({ ...props.value, [key]: value });
  const preview =
    <K extends keyof Avatar>(key: K) =>
    (value: Avatar[K]) => ({
      ...PREVIEW_BASE,
      [key]: value,
    });

  return (
    <div class="builder">
      <div class="builder-preview">
        <CatAvatar avatar={props.value} size={148} label="アバターのプレビュー" />
        <Button variant="neutral" size="sm" onClick={() => props.onChange(randomAvatar())}>
          おまかせ
        </Button>
      </div>

      <Stack gap={3} class="builder-parts">
        <ColorRow
          label="毛色"
          options={colorOptions(FUR_COLORS)}
          selected={props.value.furColor}
          onSelect={(id) => patch("furColor", id)}
        />
        <ShapeRow
          label="柄"
          options={PATTERNS}
          selected={props.value.pattern}
          preview={preview("pattern")}
          onSelect={(id) => patch("pattern", id)}
        />
        <ShapeRow
          label="顔の形"
          options={FACE_SHAPES}
          selected={props.value.shape}
          preview={preview("shape")}
          onSelect={(id) => patch("shape", id)}
        />
        <ShapeRow
          label="耳"
          options={EARS}
          selected={props.value.ears}
          preview={preview("ears")}
          onSelect={(id) => patch("ears", id)}
        />
        <ShapeRow
          label="目"
          options={EYES}
          selected={props.value.eyes}
          preview={preview("eyes")}
          onSelect={(id) => patch("eyes", id)}
        />
        <ColorRow
          label="瞳の色"
          options={colorOptions(EYE_COLORS)}
          selected={props.value.eyeColor}
          onSelect={(id) => patch("eyeColor", id)}
        />
        <ShapeRow
          label="口もと"
          options={MOUTHS}
          selected={props.value.mouth}
          preview={preview("mouth")}
          onSelect={(id) => patch("mouth", id)}
        />
        <ShapeRow
          label="ひげ"
          options={WHISKERS}
          selected={props.value.whiskers}
          preview={preview("whiskers")}
          onSelect={(id) => patch("whiskers", id)}
        />
        <ShapeRow
          label="アクセサリ"
          options={ACCESSORIES}
          selected={props.value.accessory}
          preview={preview("accessory")}
          onSelect={(id) => patch("accessory", id)}
        />
        <ColorRow
          label="背景"
          options={colorOptions(BACKGROUND_COLORS)}
          selected={props.value.background}
          onSelect={(id) => patch("background", id)}
        />
      </Stack>
    </div>
  );
}

/** 一覧などで小さく出すとき用。 */
export function AvatarChip(props: { avatar: Avatar; name: string }): JSX.Element {
  return (
    <HStack gap={2} align="center">
      <CatAvatar avatar={props.avatar} size={28} label={`${props.name} のアバター`} />
      <span>{props.name}</span>
    </HStack>
  );
}
